import { useEffect, useRef } from 'react';
import { Box, TextField, Typography } from '@mui/material';

const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

// Expands #abc to #aabbcc because input[type=color] only accepts 7-char hex.
const expandHex = value => {
  if (!HEX_RE.test(value || '')) return '#000000';
  if (value.length === 7) return value.toLowerCase();
  return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`.toLowerCase();
};

// Color swatch (native color input) combined with a hex text field. The text
// field is uncontrolled and commits on blur / Enter; invalid hex values are
// reverted to the current value.
const ColorField = ({ label, value, onChange, disabled }) => {
  const hexRef = useRef(null);

  useEffect(() => {
    if (hexRef.current) hexRef.current.value = value || '';
  }, [value]);

  const commitHex = () => {
    const raw = (hexRef.current?.value || '').trim();
    if (HEX_RE.test(raw)) {
      onChange(raw.toLowerCase());
    } else if (hexRef.current) {
      hexRef.current.value = value || '';
    }
  };

  return (
    <Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Box
          component='input'
          type='color'
          value={expandHex(value)}
          disabled={disabled}
          onChange={event => onChange(event.target.value)}
          sx={{
            width: 40,
            height: 40,
            p: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
          }}
        />
        <TextField
          size='small'
          fullWidth
          disabled={disabled}
          inputRef={hexRef}
          defaultValue={value || ''}
          placeholder='#000000'
          onBlur={commitHex}
          onKeyDown={event => {
            if (event.key === 'Enter') event.target.blur();
          }}
        />
      </Box>
    </Box>
  );
};

export default ColorField;
